/**
 * Shape tokens for `CustomTable` — the single source of truth for how every
 * data table in the app is sized, spaced and typeset.
 *
 * Taken from the Plan & Billing lists (Payment Methods / Invoices), which the
 * design treats as the canonical table. **Colours deliberately do not live
 * here** — they come from `theme.palette.table.*` so a re-skin never has to
 * touch this file. Changing a value here changes every table at once.
 */

export const customTableTokens = {
  /** Outer card chrome. */
  card: {
    radius: "14px",
    borderWidth: "1px",
  },

  /** Title / description block rendered above the column headers. */
  caption: {
    paddingTop: "20px",
    paddingBottom: "16px",
    gap: "2px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleLetterSpacing: "-0.01em",
    descriptionFontSize: "12.5px",
    descriptionFontWeight: 400,
  },

  /** Horizontal padding shared by the caption, every cell and the footer. */
  gutter: "28px",

  header: {
    paddingY: "10px",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    lineHeight: "normal",
    /** Sort arrow — the design keeps it small and low-contrast until active. */
    sortIconSize: "15px",
    iconSize: "14px",
    iconGap: "8px",
  },

  row: {
    paddingY: "13px",
    minHeight: "48px",
    fontSize: "13px",
    fontWeight: 400,
    /** Cells carrying the row's identifying value read one step stronger. */
    strongFontWeight: 600,
    lineHeight: "normal",
    /** Hover / selected fills fade rather than snap. */
    transition: "background-color 160ms ease",
  },

  /**
   * Checkbox / radio in the selection column. MUI wraps its 18px glyph in 9px
   * of padding — a 36px control, which is nearly three times the 13px text line
   * the header strip is sized around and stretches the whole header. The
   * padding is dropped so the glyph itself sets the height; head and body use
   * the same value or the two columns stop lining up.
   */
  selection: {
    padding: 0,
    glyphSize: 18,
  },

  /** Trailing action column — icon buttons sit flush to the right gutter. */
  action: {
    iconSize: "15px",
    gap: "4px",
  },

  /** Skeleton placeholder rows shown while data loads. */
  loading: {
    rowCount: 5,
    skeletonHeight: 20,
    skeletonWidth: "70%",
  },

  empty: {
    /**
     * The empty cell has to be styled from `tableSx` rather than its own `sx`:
     * the table's descendant cell rules out-specify a class on the element, so
     * a local `sx` would silently lose. This class is the hook for that.
     */
    cellClass: "CustomTable-emptyCell",
    paddingY: "34px",
    fontSize: "12.5px",
    fontWeight: 400,
    /** Height of the illustration used when no custom empty state is given. */
    imageHeight: "180px",
  },

  pagination: {
    paddingY: "14px",
    itemSize: "28px",
    itemRadius: "7px",
    itemGap: "4px",
    fontSize: "12px",
    fontWeight: 600,
    selectedFontWeight: 700,
    arrowSize: "11px",
  },

  /** Below this the table scrolls horizontally rather than squashing columns. */
  minWidth: 700,
} as const;

export default customTableTokens;
